import { Content, ContentVariants, ExpandableSection, Stack, StackItem } from "@patternfly/react-core";
import { CheckCircleIcon, ExclamationTriangleIcon, TimesCircleIcon } from "@patternfly/react-icons";
import { type FC, useState } from "react";
import { ProductListingTypes } from "../../constants/product";
import { useProductMetadata } from "../../hooks/useProductMetadata";
import {
	CombinedAttachedProjectsType,
	getAttachedProjectsCombinedType,
	getHelmOrOperatorValidator,
} from "../../utils/attachedProjectValidator";
import { DeployedAsSetType, getAttachedProjectBadge, getDeployedAsSet } from "../../utils/productDeployedAsSet";
import "./ProductComponents.scss";

export const ProductComponentsRequirements: FC = () => {
	const {
		productDetail,
		certificationListState,
		componentMetadata: {
			hasCertified,
			atLeastOnePublished,
			hasPublishedContainers,
			hasPublishedHelmCharts,
			hasPublishedOperators,
			hasPublishedCNIorCSI,
			isValid,
		},
	} = useProductMetadata();
	const [isExpanded, setIsExpanded] = useState(false);
	const onToggle = (isExpanded: boolean) => {
		setIsExpanded(isExpanded);
	};
	const getRequirements = () => {
		const combinedAttachedType = getAttachedProjectsCombinedType(certificationListState);
		const currentBadge = getAttachedProjectBadge(certificationListState);
		const projectTypeSet = getDeployedAsSet(certificationListState);
		const helmOrOperatorValidator = getHelmOrOperatorValidator(
			projectTypeSet,
			hasPublishedContainers,
			hasPublishedHelmCharts,
			hasPublishedOperators,
		);
		const getIcon = (status: boolean) =>
			status ? <CheckCircleIcon color="green" /> : <TimesCircleIcon color="#c9190b" />;
		const HelmOrObiChecks: FC = () => (
			<>
				<StackItem>
					{getIcon(projectTypeSet.has(DeployedAsSetType.helmChart) || projectTypeSet.has(DeployedAsSetType.operator))}{" "}
					Attach a helm chart or operator component
				</StackItem>
				<StackItem>
					{getIcon(projectTypeSet.has(DeployedAsSetType.containers))} Attach a container image component
				</StackItem>
				{currentBadge && ["cni", "csi"].includes(currentBadge) && (
					<StackItem>
						{getIcon(hasPublishedCNIorCSI)} Complete a {currentBadge.toUpperCase()} validation for your component
					</StackItem>
				)}
				<StackItem>
					{getIcon(helmOrOperatorValidator)} At least one container and one operator or helm chart must have a status of
					"published"
				</StackItem>
				{(projectTypeSet.has(DeployedAsSetType.helmChart) || projectTypeSet.has(DeployedAsSetType.operator)) &&
					(projectTypeSet.has(DeployedAsSetType.rhel) ||
						projectTypeSet.has(DeployedAsSetType.openstackAppContainer)) && (
						<StackItem>
							{getIcon(false)} It is not possible to attach RHEL or Openstack components with Helm or OBI components
						</StackItem>
					)}
			</>
		);
		let mainContainerTypeMessage = "To publish a containerized app as a Container Image";
		let containerSubType = "container image component";
		if (!projectTypeSet.has(DeployedAsSetType.containers)) {
			if (projectTypeSet.has(DeployedAsSetType.openstackAppContainer) && !projectTypeSet.has(DeployedAsSetType.rhel)) {
				mainContainerTypeMessage = "To publish a containerized app as a containerized OpenStack application";
				containerSubType = "containerized OpenStack component";
			}
			if (projectTypeSet.has(DeployedAsSetType.rhel) && !projectTypeSet.has(DeployedAsSetType.openstackAppContainer)) {
				mainContainerTypeMessage = "To publish a containerized app as a containerized RHEL application";
				containerSubType = "containerized RHEL component";
			}
		}
		const mainOpenStackInfraTypeMessage = "To publish an OpenStack Infrastructure product.";
		return (
			<>
				{productDetail.type === ProductListingTypes.ContainerStack && (
					<>
						{combinedAttachedType === CombinedAttachedProjectsType.container && (
							<Stack className="attached-project-status" hasGutter>
								<StackItem>
									<Content>
										<Content component={ContentVariants.p}>{mainContainerTypeMessage}</Content>
									</Content>
								</StackItem>
								<StackItem>
									<Stack className="status" hasGutter>
										<StackItem>
											{getIcon(
												projectTypeSet.has(DeployedAsSetType.containers) ||
													projectTypeSet.has(DeployedAsSetType.openstackAppContainer) ||
													projectTypeSet.has(DeployedAsSetType.rhel),
											)}{" "}
											Attach a {containerSubType}
										</StackItem>
										<StackItem>
											{getIcon(atLeastOnePublished)} At least one attached components must have a status of "published"
										</StackItem>
									</Stack>
								</StackItem>
							</Stack>
						)}
						{combinedAttachedType === CombinedAttachedProjectsType.operatorOrHelm && (
							<Stack className="attached-project-status" hasGutter>
								<StackItem>To publish a containerized app deployed as a Helm Chart or Operator Bundle</StackItem>
								<StackItem>
									<Stack className="status" hasGutter>
										<HelmOrObiChecks />
									</Stack>
								</StackItem>
							</Stack>
						)}
						{combinedAttachedType === CombinedAttachedProjectsType.openShiftCnf && (
							<Stack className="attached-project-status" hasGutter>
								<StackItem>
									<Content>
										<Content component={ContentVariants.p}>
											To publish a Certified Cloud-Native Network Function (CNF)
										</Content>
									</Content>
								</StackItem>
								<StackItem>
									<Stack className="status" hasGutter>
										<StackItem>
											{getIcon(hasCertified)} Attach a Certified Cloud-Native Network Function (CNF) component
										</StackItem>
										<HelmOrObiChecks />
										{(projectTypeSet.has(DeployedAsSetType.rhel) ||
											projectTypeSet.has(DeployedAsSetType.openstackAppContainer)) && (
											<StackItem>
												{getIcon(false)} You cannot attach a RHEL or Openstack container with a certified CNF
											</StackItem>
										)}
									</Stack>
								</StackItem>
							</Stack>
						)}
					</>
				)}
				{productDetail.type === ProductListingTypes.TraditionalApplication && (
					<Stack className="attached-project-status" hasGutter>
						<StackItem>
							<Content>
								<Content component={ContentVariants.p}>
									To publish a standalone app as a Red Hat Enterprise Linux application
								</Content>
							</Content>
						</StackItem>
						<StackItem>
							<Stack hasGutter>
								{projectTypeSet.has(DeployedAsSetType.rhelNonContainer) && (
									<StackItem>
										<Content component={ContentVariants.p}>
											{getIcon(true)} Attach a Red Hat Enterprise Linux Application component
										</Content>
									</StackItem>
								)}
								<StackItem>
									{getIcon(atLeastOnePublished)} At least one attached components must have a status of "published"
								</StackItem>
							</Stack>
						</StackItem>
					</Stack>
				)}
				{productDetail.type === ProductListingTypes.OpenStackInfrastructure && (
					<Stack className="attached-project-status" hasGutter>
						<StackItem>
							<Content>
								<Content component={ContentVariants.p}>{mainOpenStackInfraTypeMessage}</Content>
							</Content>
						</StackItem>
						<StackItem>
							<Stack hasGutter>
								{(projectTypeSet.has(DeployedAsSetType.openStackInfraContainer) ||
									projectTypeSet.has(DeployedAsSetType.openStackInfraNonContainer)) && (
									<StackItem>
										<Content component={ContentVariants.p}>
											{getIcon(true)} Attach a OpenStack Infrastructure component
										</Content>
									</StackItem>
								)}
								{projectTypeSet.has(DeployedAsSetType.openstackVNF) && (
									<StackItem>
										<Content component={ContentVariants.p}>
											{getIcon(true)} Attach a VNF for OpenStack component
										</Content>
									</StackItem>
								)}
								{projectTypeSet.has(DeployedAsSetType.openstackVNF) ? (
									<StackItem>
										{getIcon(atLeastOnePublished)} The attached component must be validated or certified and have a
										status of "published"
									</StackItem>
								) : (
									<StackItem>
										{getIcon(atLeastOnePublished)} At least one attached components must have a status of "published"
									</StackItem>
								)}
							</Stack>
						</StackItem>
					</Stack>
				)}
			</>
		);
	};

	return (
		<ExpandableSection
			className="product-requirements__component-expandable"
			toggleContent={
				isValid ? (
					<div>
						<CheckCircleIcon color="green" />
						<span>&nbsp;Component requirements are met to publish product</span>
					</div>
				) : (
					<div>
						<ExclamationTriangleIcon color="#f1ac01" />
						<span>&nbsp;Component requirements are not met to publish product</span>
					</div>
				)
			}
			onToggle={(_event, isExpanded: boolean) => onToggle(isExpanded)}
			isExpanded={isExpanded}
			displaySize="lg"
			isWidthLimited
		>
			{getRequirements()}
		</ExpandableSection>
	);
};
